import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Droplets, MapPin, Zap, Clock, ArrowRight, FileCheck2, Inbox } from "lucide-react";

export default function ContractorDashboard() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState(null);
  const [tab, setTab] = useState("pending");

  useEffect(() => {
    api.get("/jobs").then(({ data }) => setJobs(data)).catch(() => setJobs([]));
  }, []);

  const filtered = (jobs || []).filter((j) =>
    tab === "pending" ? j.status !== "invoiced" : j.status === "invoiced"
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-400">
            Meter Reader Dashboard
          </span>
          <h1 className="font-display text-3xl font-600 tracking-tight text-slate-900 mt-1">
            Welcome back, {user?.name?.split(" ")[0] || "Contractor"}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Pick a job below to upload meter evidence and generate an invoice.
          </p>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          <StatCard label="Open Jobs" value={(jobs || []).filter((j) => j.status !== "invoiced").length} icon={<Inbox className="w-5 h-5" />} />
          <StatCard label="Invoiced" value={(jobs || []).filter((j) => j.status === "invoiced").length} icon={<FileCheck2 className="w-5 h-5" />} />
          <StatCard label="Basic Fee" value="$115" icon={<Clock className="w-5 h-5" />} />
          <StatCard label="Urgent Fee" value="+$35" icon={<Zap className="w-5 h-5" />} />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-10 border-b border-slate-200">
          <TabBtn active={tab === "pending"} onClick={() => setTab("pending")} testid="tab-pending">
            Open jobs
          </TabBtn>
          <TabBtn active={tab === "invoiced"} onClick={() => setTab("invoiced")} testid="tab-invoiced">
            Invoiced
          </TabBtn>
        </div>

        {/* Job list */}
        <div className="mt-6 space-y-3" data-testid="job-list">
          {jobs === null ? (
            <div className="py-16 text-center text-slate-400">
              <Droplets className="w-7 h-7 mx-auto animate-pulse text-sky-400" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-slate-400 bg-white border border-dashed border-slate-200 rounded-xl">
              <Inbox className="w-8 h-8 mx-auto mb-3" />
              <p className="text-sm">No {tab === "pending" ? "open" : "invoiced"} jobs yet.</p>
            </div>
          ) : (
            filtered.map((job) => <JobRow key={job.id} job={job} />)
          )}
        </div>
      </div>
    </div>
  );
}

function JobRow({ job }) {
  const urgent = job.service_type === "urgent";
  return (
    <div
      data-testid={`job-row-${job.id}`}
      className="group bg-white border border-slate-200 rounded-xl p-5 flex items-center justify-between hover:border-slate-300 hover:shadow-md transition-all"
    >
      <div className="flex items-start gap-4 min-w-0">
        <div className="w-10 h-10 rounded-lg bg-sky-50 flex items-center justify-center shrink-0">
          <MapPin className="w-5 h-5 text-sky-500" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-slate-900 truncate">{job.property_address}</span>
            {job.matter_number && (
              <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                Matter {job.matter_number}
              </span>
            )}
            {urgent ? (
              <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-red-50 text-red-500 flex items-center gap-1">
                <Zap className="w-3 h-3" /> Urgent
              </span>
            ) : (
              <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                Standard
              </span>
            )}
          </div>
          <div className="text-sm text-slate-500 mt-0.5 truncate">
            Purchaser: {job.purchaser_name} • Vendor: {job.vendor_name}
          </div>
          <div className="text-xs text-slate-400 mt-0.5">
            Requested by {job.solicitor_name}
            {job.settlement_date ? ` • Settlement ${job.settlement_date}` : ""}
          </div>
          {job.notes && <div className="text-xs text-slate-400 mt-1 truncate max-w-md">“{job.notes}”</div>}
        </div>
      </div>

      {job.status === "invoiced" ? (
        <span className="text-xs font-semibold text-emerald-600 flex items-center gap-1.5 shrink-0">
          <FileCheck2 className="w-4 h-4" /> Invoiced
        </span>
      ) : (
        <Link
          to={`/contractor/invoice/${job.id}`}
          data-testid={`generate-invoice-${job.id}`}
          className="shrink-0 inline-flex items-center gap-1.5 text-sm font-medium bg-slate-900 text-white px-4 py-2 rounded-md hover:bg-sky-600 transition-colors"
        >
          Generate invoice <ArrowRight className="w-4 h-4" />
        </Link>
      )}
    </div>
  );
}

function StatCard({ label, value, icon }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-center justify-between text-slate-400">
        <span className="text-xs tracking-[0.15em] uppercase font-semibold">{label}</span>
        {icon}
      </div>
      <div className="font-display text-2xl font-700 text-slate-900 mt-2">{value}</div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors ${
        active ? "border-sky-500 text-sky-600" : "border-transparent text-slate-500 hover:text-slate-800"
      }`}
    >
      {children}
    </button>
  );
}
